import { CSSProperties } from "react";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faHandshake,
  faTag,
  faTrophy,
  faUserTie,
} from "@fortawesome/free-solid-svg-icons";
import { CompleteBooking } from "../../types/Booking";
import { BookingStatus, BookingType } from "../../enums/BookingScope";
import { getBookingTypeLabel } from "../../utils/stringUtil";
import { getBookingBlockStyle } from "../../utils/bookingColor";
import "./BookingBlock.css";

const TYPE_ICONS = {
  [BookingType.Friendly]: faHandshake,
  [BookingType.Tournament]: faTrophy,
  [BookingType.Training]: faUserTie,
};

type Props = {
  booking: CompleteBooking;
  /** Larghezza esplicita (modalità libera: proporzionale alla durata). */
  width?: number;
  /** Righe con i giocatori della partita, se ci sono (modalità libera). */
  players?: string[];
  onClick?: (e: React.MouseEvent) => void;
  className?: string;
  style?: CSSProperties;
};

const fullName = (user?: { name?: string; familyName?: string } | null) =>
  user ? `${user.name || ""} ${user.familyName || ""}`.trim() : "";

/**
 * Blocco di una prenotazione nel calendario del gestionale (slot e modalità
 * libera): orario, icona del tipo, titolo (note della struttura o utente),
 * sottotitolo (tipo / maestro) ed eventuale spunta di completata. Con una
 * tipologia personalizzata il titolo è la tipologia e le note scendono nel
 * sottotitolo. Il colore viene dal tipo (classi) o da quello scelto dal
 * gestore (style inline).
 */
export default function BookingBlock({
  booking,
  width,
  players,
  onClick,
  className,
  style,
}: Props) {
  const typeLabel = getBookingTypeLabel(booking);
  const hasCustomType = !!booking.customType;
  const coachName = fullName(booking.coach);
  const title = booking.createdByHost
    ? !hasCustomType && booking.notes && booking.notes !== ""
      ? booking.notes
      : typeLabel
    : fullName(booking.user) || "Utente sconosciuto";
  const subtitle = booking.createdByHost
    ? hasCustomType
      ? booking.notes || "Struttura"
      : booking.bookingType === BookingType.Training && coachName
      ? `Maestro ${coachName}`
      : "Struttura"
    : booking.bookingType === BookingType.Training && coachName
    ? `${typeLabel} · ${coachName}`
    : typeLabel;
  const completed = booking.status === BookingStatus.Completed;

  return (
    <div
      onClick={onClick}
      style={{ width, ...getBookingBlockStyle(booking.color), ...style }}
      className={classNames(
        "bk-block",
        {
          "bk-block--friendly":
            !hasCustomType && booking.bookingType === BookingType.Friendly,
          "bk-block--lesson":
            !hasCustomType && booking.bookingType === BookingType.Training,
          "bk-block--tournament":
            !hasCustomType && booking.bookingType === BookingType.Tournament,
          "bk-block--custom-type": hasCustomType,
          "bk-block--custom": !!booking.color,
          "bk-block--completed": completed,
        },
        className
      )}
    >
      <div className="bk-block-top">
        <span className="bk-block-time">{booking.time}</span>
        <span className="bk-block-icon" title={typeLabel}>
          <FontAwesomeIcon
            icon={
              hasCustomType
                ? faTag
                : TYPE_ICONS[booking.bookingType] || faHandshake
            }
          />
        </span>
      </div>

      {players && players.length > 0 ? (
        <div className="bk-block-players">
          {players.map((name, i) =>
            name === "vs" ? (
              <span key={i} className="bk-block-vs">
                vs
              </span>
            ) : (
              <span key={i} className="bk-block-player truncate-text">
                {name}
              </span>
            )
          )}
        </div>
      ) : (
        <>
          <span className="bk-block-title truncate-text">{title}</span>
          <span className="bk-block-sub truncate-text">{subtitle}</span>
        </>
      )}

      {completed && (
        <span className="bk-block-check" title="Completata">
          <FontAwesomeIcon icon={faCheck} />
        </span>
      )}
    </div>
  );
}
